/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { useForm, useFormContext, useWatch } from '@repo/shared-client/form'
import { useCallback, useEffect, useRef } from 'react'

import type { EventListFilterFormValues } from './event-list-filter-types'

export function useEventListFilterForm(filterDefaults: EventListFilterFormValues) {
  const formMethods = useForm<EventListFilterFormValues>({
    defaultValues: filterDefaults,
  })
  const previousDefaultDistanceRef = useRef<EventListFilterFormValues['distance']>(undefined)

  useEffect(() => {
    formMethods.reset(filterDefaults, { keepDirtyValues: true })

    const nextDefaultDistance = filterDefaults.distance
    const previousDefaultDistance = previousDefaultDistanceRef.current
    const distanceDefaultChanged = previousDefaultDistance?.[0] !== nextDefaultDistance?.[0]

    if (distanceDefaultChanged) {
      formMethods.setValue('distance', nextDefaultDistance, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      })
    }

    previousDefaultDistanceRef.current = nextDefaultDistance
  }, [filterDefaults, formMethods])

  return formMethods
}

export function useEventListFilterFields(onFiltersApply: (values: EventListFilterFormValues) => void) {
  const { getValues } = useFormContext<EventListFilterFormValues>()

  const triggerFilterChange = useCallback(
    (patch?: Record<string, unknown>) => {
      onFiltersApply({ ...getValues(), ...patch } as EventListFilterFormValues)
    },
    [getValues, onFiltersApply]
  )

  const distance = useWatch({ name: 'distance' })

  const handleSearchChange = useCallback(
    (value: string) => {
      triggerFilterChange({ name: value })
    },
    [triggerFilterChange]
  )

  return { triggerFilterChange, distance, handleSearchChange }
}
